import { useState, useEffect } from 'react';

/**
 * Synchronously check if the device is mobile.
 * This is used for the initial state to avoid race conditions.
 *
 * Uses multiple detection methods for better accuracy:
 * 1. User Agent string (detects mobile OS)
 * 2. Touch capability
 * 3. Device pixel ratio (mobile devices usually have higher DPR)
 * 4. Screen dimensions as a fallback
 */
const checkIfMobile = () => {
    if (typeof window === 'undefined') return false;

    // Method 1: Check user agent for mobile devices
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent.toLowerCase());

    // Method 2: Check for touch capability
    const hasTouchScreen = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
    );

    // Method 3: Check device pixel ratio (mobile devices usually have DPR > 1)
    const hasHighDPR = window.devicePixelRatio > 1;

    // Method 4: Check screen dimensions (fallback)
    // Use a higher threshold and consider orientation
    const isSmallScreen = window.innerWidth < 1024 || window.innerHeight < 600;

    // A device is considered mobile if:
    // - User agent indicates mobile OS, OR
    // - Has touch screen AND (high DPR OR small screen)
    return isMobileUA || (hasTouchScreen && (hasHighDPR || isSmallScreen));
};

const useIsMobileDevice = () => {
    // Initialize with synchronous check to avoid race conditions
    const [isMobile, setIsMobile] = useState(() => checkIfMobile());

    useEffect(() => {
        const handleChange = () => setIsMobile(checkIfMobile());

        // Re-check on resize or orientation change
        window.addEventListener('resize', handleChange);
        window.addEventListener('orientationchange', handleChange);

        return () => {
            window.removeEventListener('resize', handleChange);
            window.removeEventListener('orientationchange', handleChange);
        };
    }, []);

    return isMobile;
};

export default useIsMobileDevice;
